import RssFeedIcon from "@mui/icons-material/RssFeed";
import { DialogActions, DialogContent } from "@mui/material";
import api from "app/api";
import { ROLES } from "app/constants";
import { useAppSelector } from "app/hooks";
import {
  Autocomplete,
  Button,
  CreatedUpdatedBy,
  Field,
  FieldsContainer,
  File,
  Form,
  PageHeader,
  TextField,
  useDownloadUrl,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import HistoryListDialog from "features/histories/HistoryListDialog";
import { selectProfile } from "features/profile/profileSlice";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { createContext } from "react";
import usePartnerFeedForm, {
  UsePartnerFeedFormProps,
  UsePartnerFeedFormReturnType,
} from "./usePartnerFeedForm";

export const PartnerFeedContext = createContext<
  UsePartnerFeedFormReturnType | undefined
>(undefined);

export interface PartnerFeedFormProps extends UsePartnerFeedFormProps {}

export default function PartnerFeedForm({
  id,
  afterSave,
  onClose,
  defaultValues,
}: PartnerFeedFormProps) {
  const { hasPermissions, hasRoles, canEditItem } = useAccess();
  const profile = useAppSelector(selectProfile);
  const { downloadUrl } = useDownloadUrl();
  const uploadService = api.getService("/files");

  const isPartner = hasRoles(ROLES.PARTNER);
  const hasViewAny = hasPermissions("partner_feed.viewany");
  const hasViewOwn = hasPermissions("partner_feed.viewown") && !hasViewAny;

  const form = usePartnerFeedForm({
    id,
    afterSave,
    defaultValues: hasViewOwn
      ? { ...defaultValues, account_manager_id: profile.id }
      : defaultValues,
  });
  const {
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
  } = form;
  const title = isCreate ? I18n.t("PartnerFeed.addTitle") : item.title;
  const subtitle = isCreate ? undefined : undefined;

  return (
    <ParamsContext.Provider value={{ partner_feed_id: id }}>
      <PartnerFeedContext.Provider value={form}>
        <PageHeader
          avatar={<RssFeedIcon />}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          loading={loading}
        />
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="PartnerFeed.attributes"
            translationPlaceholdersCategory="PartnerFeed.placeholders"
            translationHelpersCategory="PartnerFeed.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={!canEditItem("partner_feed", item)}
          >
            <FieldsContainer>
              <Field fullWidth>
                <TextField id="title" />
              </Field>
              <Field fullWidth>
                <TextField id="description" multiline rows={4} />
              </Field>
              {!isPartner && (
                <Field fullWidth>
                  <Autocomplete id="account_manager_id" disabled={hasViewOwn} />
                </Field>
              )}
              <Field fullWidth>
                <File
                  id="file_id"
                  uri={downloadUrl(item.file_id)}
                  upload={uploadService.uploadFile}
                  file={item.file}
                  accept="application/pdf"
                />
              </Field>
            </FieldsContainer>
          </Form>
        </DialogContent>
        <DialogActions>
          {isUpdate && (
            <CreatedUpdatedBy
              objectName="PartnerFeed"
              item={item}
              sx={{
                p: 1,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          )}
          <Button
            title={I18n.t("App.save")}
            disabled={!isDirty}
            onClick={() => save()}
            loading={saving}
          />
        </DialogActions>
      </PartnerFeedContext.Provider>
    </ParamsContext.Provider>
  );
}
